import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDownIcon, X } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { FILTER_ALL_VALUE, isFilterSet } from "../../utils/filters";

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterDefinition<K extends string = string> {
  key: K;
  label: string;
  options: FilterOption[];
  searchable?: boolean;
  includeAllOption?: boolean;
  defaultValue?: string;
  render?: (id: string) => ReactNode;
  // How a set value reads in a summary of the active filters, when its option label won't do
  // (e.g. a multi-select): see the offers' `describeActiveFilters`.
  formatValue?: (value: string) => string;
}

interface FilterBarProps<K extends string> {
  definitions: FilterDefinition<K>[];
  values: Record<K, string>;
  onChange: (patch: Partial<Record<K, string>>) => void;
  onClear: () => void;
}

interface SearchableFilterProps {
  id: string;
  value: string;
  options: FilterOption[];
  includeAllOption: boolean;
  onChange: (value: string) => void;
}

const SearchableFilter = ({
  id,
  value,
  options,
  includeAllOption,
  onChange,
}: SearchableFilterProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const choices = includeAllOption
    ? [{ value: FILTER_ALL_VALUE, label: t("filters.all") }, ...options]
    : options;
  const selectedLabel = choices.find(option => option.value === value)?.label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <WFPButton
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className={cn("line-clamp-1", !selectedLabel && "text-muted-foreground")}>
            {selectedLabel ?? t("common.select")}
          </span>
          <ChevronDownIcon className="opacity-50" />
        </WFPButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-0">
        <Command>
          <CommandInput placeholder={t("common.search")} />
          <CommandList className="max-h-72">
            <CommandEmpty>{t("filters.noResults")}</CommandEmpty>
            <CommandGroup>
              {choices.map(option => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label]}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <span className="flex-1">{option.label}</span>
                  {option.value === value && <Check />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const FilterBar = <K extends string>({
  definitions,
  values,
  onChange,
  onClear,
}: FilterBarProps<K>) => {
  const { t } = useTranslation();

  const hasActiveFilter = definitions.some(
    ({ key, defaultValue }) => isFilterSet(values[key]) && values[key] !== defaultValue
  );

  return (
    <Card className="gap-0 p-4">
      <div className="flex flex-wrap items-end gap-3">
        {definitions.map(({ key, label, options, searchable, includeAllOption = true, render }) => (
          <div key={key} className="flex min-w-44 flex-1 flex-col gap-1.5">
            <Label htmlFor={`filter-${key}`} className="font-semibold text-foreground text-sm">
              {label}
            </Label>
            {render ? (
              render(`filter-${key}`)
            ) : searchable ? (
              <SearchableFilter
                id={`filter-${key}`}
                value={values[key]}
                options={options}
                includeAllOption={includeAllOption}
                onChange={value => onChange({ [key]: value } as Partial<Record<K, string>>)}
              />
            ) : (
              <Select
                value={values[key]}
                onValueChange={value => onChange({ [key]: value } as Partial<Record<K, string>>)}
              >
                <SelectTrigger id={`filter-${key}`} className="w-full border-border">
                  <SelectValue placeholder={t("common.select")} />
                </SelectTrigger>
                <SelectContent position="popper" className="max-h-72">
                  {includeAllOption && (
                    <SelectItem value={FILTER_ALL_VALUE}>{t("filters.all")}</SelectItem>
                  )}
                  {options.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        ))}

        <div className="ms-auto flex shrink-0 items-center gap-1">
          {hasActiveFilter && (
            <WFPButton type="button" variant="ghost" onClick={onClear}>
              <X />
              {t("filters.clearAll")}
            </WFPButton>
          )}
        </div>
      </div>
    </Card>
  );
};
